import React from 'react';

import { useChatModeState } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeState';
import { getGreetingModes } from '@atlassian/conversation-assistant-ui-components/ui/getGreetingModes';
import { RovoGreeting } from '@atlassian/conversation-assistant-ui-components/ui/RovoGreeting';
import {
	type UsePersonalityGreetingParams,
	usePersonalityGreeting,
} from '@atlassian/rovo-personalization/personality-greeting/usePersonalityGreeting';

type PersonalityRovoGreetingProps = {
	firstName: string;
	/** Conversation id whose reasoning mode drives the greeting bucket. */
	conversationId: string;
	headingSize?: React.ComponentProps<typeof RovoGreeting>['headingSize'];
	isRevealedByResize?: boolean | null;
	isRevealedByDefault?: boolean;
	assistanceServiceParams: UsePersonalityGreetingParams['assistanceServiceParams'];
};

/**
 * `RovoGreeting` wrapper that resolves and applies the active preset's greeting.
 * Rendered only when the personality-greeting gate is on (see `RovoEmptyState`),
 * which keeps the user-settings read scoped to gated users.
 *
 * Reads the reasoning mode from `conversationId` — the same key the composer and
 * mode pill use — so the greeting reflects the live selection. Keeps the plain
 * per-mode greeting visible until the preset resolves.
 */
export const PersonalityRovoGreeting = ({
	firstName,
	conversationId,
	headingSize,
	isRevealedByResize,
	isRevealedByDefault,
	assistanceServiceParams,
}: PersonalityRovoGreetingProps): React.JSX.Element => {
	const { deepResearchEnabled, thinkDeeperEnabled, taskModeEnabled } =
		useChatModeState(conversationId);

	const { personalityMode, presentationMode } = getGreetingModes({
		deepResearchEnabled,
		thinkDeeperEnabled,
		taskModeEnabled,
	});

	const { greeting, isPending } = usePersonalityGreeting({
		mode: personalityMode,
		firstName,
		assistanceServiceParams,
	});

	return (
		<RovoGreeting
			firstName={firstName}
			isDeepResearchEnabled={deepResearchEnabled}
			isTaskModeEnabled={taskModeEnabled}
			greetingMode={presentationMode}
			headingSize={headingSize}
			isRevealedByResize={isRevealedByResize}
			isRevealedByDefault={isRevealedByDefault}
			greetingText={isPending ? undefined : greeting?.title}
			illustrationSrc={isPending ? undefined : greeting?.src}
			illustrationSrcDark={isPending ? undefined : greeting?.srcDark}
		/>
	);
};
